import Vue from 'vue'
import Router from 'vue-router'

import LoginPage from '@/pages/Login/LoginPage.vue'
import LogoutPage from '@/pages/Logout/LogoutPage.vue'
import ActivatePage from '@/pages/Activate/ActivatePage.vue'
import RegisterPage from '@/pages/Register/RegisterPage.vue'
import ActivateSentPage from '@/pages/Activate/ActivateSentPage.vue'
import PartnerSSOPage from '@/pages/Partner/PartnerSSOPage.vue'
import AuthenticatePage from '@/pages/Authenticate/AuthenticatePage.vue'
import ResetPasswordPage from '@/pages/ResetPassword/ResetPasswordPage.vue'
import ResetPasswordSentPage from '@/pages/ResetPassword/ResetPasswordSentPage.vue'
import ResetPasswordUpdatePage from '@/pages/ResetPassword/ResetPasswordUpdatePage.vue'
import AuthenticatedPage from '@/pages/Authenticate/AuthenticatedPage.vue'
import AuthorizePage from '@/pages/Authenticate/AuthorizePage.vue'
import PasskeyEnrollmentPage from '@/pages/Passkey/PasskeyEnrollmentPage.vue'
import PasskeyStepUpPage from '@/pages/Passkey/PasskeyStepUpPage.vue'
import PasskeyLoginPage from '@/pages/Passkey/PasskeyLoginPage.vue'

// use eager loading for now, as we need to listen to onReady event to handle this.$route.query property
// see: https://github.com/vuejs/vue-router/issues/3439
// eg: implement handleRouteLanguage
/*
  router.onReady(() => {
    handleRouteLanguage(router.currentRoute)
  })
*/
import VerifyEmailPage from '@/pages/Verify/VerifyEmailPage.vue'
import { useRoute } from './guards/route-state'

import { setPageMeta } from './utils'

import * as guards from './guards'

const MfaSetupPage = () => import('@/pages/MFA/MfaSetupPage.vue')
const MfaVerificationPage = () => import('@/pages/MFA/MfaVerificationPage.vue')
const MfaResetPage = () => import('@/pages/MFA/MfaResetPage.vue')
const TrustedDeviceSetupPage = () => import('@/pages/TrustedDevice/TrustedDeviceSetupPage.vue')
const TrustedDeviceVerificationPage = () => import('@/pages/TrustedDevice/TrustedDeviceVerificationPage.vue')
const TrustedDeviceConfirmationPage = () => import('@/pages/TrustedDevice/pages/TrustedDeviceConfirmationPage.vue')
const TrustedDeviceDenyPage = () => import('@/pages/TrustedDevice/pages/TrustedDeviceDenyPage.vue')
const TrustedDeviceDeniedPage = () => import('@/pages/TrustedDevice/pages/TrustedDeviceDeniedPage.vue')
const TrustedDeviceBlockedPage = () => import('@/pages/TrustedDevice/pages/TrustedDeviceBlockedPage.vue')
const TrustedDeviceConfirmationExpiredPage = () => import('@/pages/TrustedDevice/pages/TrustedDeviceConfirmationExpiredPage.vue')
const TrustedDeviceConfirmationAlreadyActionedPage = () => import('@/pages/TrustedDevice/pages/TrustedDeviceConfirmationAlreadyActionedPage.vue')
const UnknownErrorPage = () => import('@/pages/Error/UnknownErrorPage.vue')
const AccountDisabledErrorPage = () => import('@/pages/Error/AccountDisabledErrorPage.vue')
const ActivateExpiredErrorPage = () => import('@/pages/Error/ActivateExpiredErrorPage.vue')
const PasswordExpiredErrorPage = () => import('@/pages/Error/PasswordExpiredErrorPage.vue')
const PasswordWillExpireErrorPage = () => import('@/pages/Error/PasswordWillExpireErrorPage.vue')
const NotFoundErrorPage = () => import('@/pages/Error/NotFoundErrorPage.vue')
const VerifyExpiredErrorPage = () => import('@/pages/Error/VerifyExpiredErrorPage.vue')

Vue.use(Router)

const router = new Router({
  mode: 'history',
  routes: [
    {
      path: '/',
      name: 'Root',
      redirect: { name: 'LoginPage' },
    },
    {
      path: '/authenticate/',
      name: 'AuthenticatePage',
      component: AuthenticatePage,
    },
    // must stay above /login/:stage?, which would otherwise match /login/passkey as a stage.
    // /login/passkey/cancelled is Universal Login's back-to-login link (PASSKEY_LOGIN_CANCELLED_PATH)
    {
      path: '/login/passkey/:outcome(cancelled)?',
      name: 'PasskeyLoginPage',
      component: PasskeyLoginPage,
    },
    {
      path: '/login/:stage?',
      name: 'LoginPage',
      component: LoginPage,
    },
    {
      path: '/logout',
      name: 'LogoutPage',
      component: LogoutPage,
    },
    {
      path: '/activate/',
      name: 'ActivatePage',
      component: ActivatePage,
    },
    {
      path: '/register/',
      name: 'RegisterPage',
      component: RegisterPage,
    },
    {
      path: '/activate/sent/',
      name: 'ActivateSentPage',
      component: ActivateSentPage,
    },
    {
      path: '/partners/:partnerCode/sso/:token',
      name: 'PartnerSSOPage',
      component: PartnerSSOPage,
    },
    {
      path: '/reset-password/',
      name: 'ResetPasswordPage',
      component: ResetPasswordPage,
    },
    {
      path: '/reset-password/sent',
      name: 'ResetPasswordSentPage',
      component: ResetPasswordSentPage,
    },
    {
      path: '/reset-password/update',
      name: 'ResetPasswordUpdatePage',
      component: ResetPasswordUpdatePage,
    },
    {
      path: '/error/',
      name: 'UnknownErrorPage',
      component: UnknownErrorPage,
    },
    {
      path: '/error/account-disabled/',
      name: 'AccountDisabledErrorPage',
      component: AccountDisabledErrorPage,
    },
    {
      path: '/error/activate-expired/',
      name: 'ActivateExpiredErrorPage',
      component: ActivateExpiredErrorPage,
    },
    {
      path: '/error/password-expired/',
      name: 'PasswordExpiredErrorPage',
      component: PasswordExpiredErrorPage,
    },
    {
      path: '/error/password-will-expire/',
      name: 'PasswordWillExpireErrorPage',
      component: PasswordWillExpireErrorPage,
    },
    {
      path: '/error/verify-expired/',
      name: 'VerifyExpiredErrorPage',
      component: VerifyExpiredErrorPage,
    },
    {
      path: '/mfa/reset',
      name: 'MfaResetPage',
      component: MfaResetPage,
    },
    {
      path: '/mfa/verification',
      name: 'MfaVerificationPage',
      component: MfaVerificationPage,
    },
    {
      path: '/mfa',
      name: 'MfaSetupPage',
      component: MfaSetupPage,
    },
    {
      path: '/authenticated',
      name: 'AuthenticatedPage',
      component: AuthenticatedPage,
    },
    {
      path: '/authorize',
      name: 'AuthorizePage',
      component: AuthorizePage,
    },
    {
      path: '/verify-email/',
      name: 'VerifyEmailPage',
      component: VerifyEmailPage,
    },
    {
      path: '/passkey-enrollment',
      name: 'PasskeyEnrollmentPage',
      component: PasskeyEnrollmentPage,
    },
    {
      path: '/passkey-step-up',
      name: 'PasskeyStepUpPage',
      component: PasskeyStepUpPage,
    },
    {
      path: '/trusted-device-setup',
      name: 'TrustedDeviceSetupPage',
      component: TrustedDeviceSetupPage,
    },
    {
      path: '/trusted-device/verification',
      name: 'TrustedDeviceVerificationPage',
      component: TrustedDeviceVerificationPage,
    },
    {
      path: '/trusted-device/confirmation',
      name: 'TrustedDeviceConfirmationPage',
      component: TrustedDeviceConfirmationPage,
    },
    {
      path: '/trusted-device/deny',
      name: 'TrustedDeviceDenyPage',
      component: TrustedDeviceDenyPage,
    },
    {
      path: '/trusted-devices/denied',
      name: 'TrustedDeviceDeniedPage',
      component: TrustedDeviceDeniedPage,
    },
    {
      path: '/trusted-devices/expired',
      name: 'TrustedDeviceConfirmationExpiredPage',
      component: TrustedDeviceConfirmationExpiredPage,
    },
    {
      path: '/trusted-devices/not-active',
      name: 'TrustedDeviceConfirmationAlreadyActionedPage',
      component: TrustedDeviceConfirmationAlreadyActionedPage,
    },
    {
      path: '/trusted-devices/blocked',
      name: 'TrustedDeviceBlockedPage',
      component: TrustedDeviceBlockedPage,
    },
    {
      path: '*',
      name: 'NotFoundErrorPage',
      component: NotFoundErrorPage,
    },
  ],
})

router.afterEach(setPageMeta)
router.afterEach(guards.xcomMessenger)
router.afterEach(guards.routeState)

export const useRouter = () => router

export { router, useRoute }
